/**
 * EPX-Flow Theme System
 *
 * Centralized color definitions for all BPMN shapes, edges, and UI components.
 * Users can provide partial overrides — anything not specified falls back to DEFAULT_THEME.
 */

export const DEFAULT_THEME = {
  // ─── Events ───────────────────────────────────────────────────────
  startEvent:              { stroke: '#52c41a', fill: '#f6ffed', strokeWidth: 1.5 },
  endEvent:                { stroke: '#f5222d', fill: '#fff1f0', strokeWidth: 3 },
  intermediateEvent:       { stroke: '#1890ff', fill: '#ffffff', strokeWidth: 1 },
  intermediateThrowEvent:  { stroke: '#1890ff', fill: '#ffffff', strokeWidth: 1 },
  boundaryEvent:           { stroke: '#fa541c', fill: '#fff2e8', strokeWidth: 1.5 },

  // ─── Tasks ────────────────────────────────────────────────────────
  userTask:         { stroke: '#1890ff', fill: '#e6f7ff', strokeWidth: 1 },
  serviceTask:      { stroke: '#722ed1', fill: '#f9f0ff', strokeWidth: 1 },
  scriptTask:       { stroke: '#fa8c16', fill: '#fff7e6', strokeWidth: 1 },
  manualTask:       { stroke: '#13c2c2', fill: '#e6fffb', strokeWidth: 1 },
  sendTask:         { stroke: '#1d39c4', fill: '#f0f5ff', strokeWidth: 1 },
  receiveTask:      { stroke: '#40a9ff', fill: '#e6f7ff', strokeWidth: 1 },
  businessRuleTask: { stroke: '#d48806', fill: '#fffbe6', strokeWidth: 1 },
  callActivity:     { stroke: '#389e0d', fill: '#f6ffed', strokeWidth: 3 },

  // ─── Gateways ─────────────────────────────────────────────────────
  exclusiveGateway:  { stroke: '#faad14', fill: '#fffbe6', strokeWidth: 2 },
  parallelGateway:   { stroke: '#13c2c2', fill: '#e6fffb', strokeWidth: 2 },
  inclusiveGateway:  { stroke: '#eb2f96', fill: '#fff0f6', strokeWidth: 2 },
  complexGateway:    { stroke: '#595959', fill: '#fafafa', strokeWidth: 2 },
  eventBasedGateway: { stroke: '#389e0d', fill: '#f6ffed', strokeWidth: 2 },

  // ─── Containers ───────────────────────────────────────────────────
  subProcess:   { stroke: '#096dd9', fill: '#e6f7ff', strokeWidth: 2 },
  transaction:  { stroke: '#096dd9', fill: '#e6f7ff', strokeWidth: 2 },
  participant:  { stroke: '#000',    fill: '#f5f5f5', strokeWidth: 2 },
  lane:         { stroke: '#000',    fill: '#fff',    strokeWidth: 1 },

  // ─── Data & Artifacts ─────────────────────────────────────────────
  dataObjectReference: { stroke: '#555', fill: '#fff',  strokeWidth: 1.5 },
  dataStoreReference:  { stroke: '#555', fill: '#fff',  strokeWidth: 1.5 },
  group:               { stroke: '#555', fill: 'none',  strokeWidth: 1.5 },
  textAnnotation:      { stroke: '#555', fill: 'none',  strokeWidth: 1.5 },

  // ─── Edges ────────────────────────────────────────────────────────
  sequenceFlow:    { stroke: '#555',    strokeWidth: 1.5 },
  messageFlow:     { stroke: '#1890ff', strokeWidth: 1.5 },
  association:     { stroke: '#555',    strokeWidth: 1 },
  dataAssociation: { stroke: '#555',    strokeWidth: 1 },

  // ─── Canvas ───────────────────────────────────────────────────────
  grid: { color: '#e0e0e0' },
  canvas: { background: '#fff' },

  // ─── Text ─────────────────────────────────────────────────────────
  text: { fill: '#333' },

  // ─── UI Panel (properties, toolbar) ───────────────────────────────
  panel: {
    background: '#fff',
    headerBackground: '#fff',
    headerBorder: '#ccc',
    text: '#333',
    textSecondary: '#555',
    textMuted: '#888',
    border: '#e8e8e8',
    inputBackground: '#fff',
    inputBorder: '#ccc',
    hoverBackground: '#f8f9fa',
    accentBackground: '#e6f7ff',
    accentBorder: '#bae7ff',
    accentText: '#1890ff',
  },
};

/**
 * Deep-merge a partial theme into DEFAULT_THEME.
 * Only merges two levels deep (element → property).
 */
export function mergeTheme(custom) {
  if (!custom) return { ...DEFAULT_THEME };
  const merged = {};
  for (const key of Object.keys(DEFAULT_THEME)) {
    if (typeof DEFAULT_THEME[key] === 'object' && DEFAULT_THEME[key] !== null) {
      merged[key] = { ...DEFAULT_THEME[key], ...(custom[key] || {}) };
    } else {
      merged[key] = custom[key] !== undefined ? custom[key] : DEFAULT_THEME[key];
    }
  }
  // Allow custom keys not in DEFAULT_THEME
  for (const key of Object.keys(custom)) {
    if (!(key in merged) && typeof custom[key] === 'object') {
      merged[key] = { ...custom[key] };
    }
  }
  return merged;
}

/**
 * Get theme colors for a specific element type from a LogicFlow model instance.
 * Models access the theme stored on the graphModel by EpxFlow.
 */
export function getThemeStyle(graphModel, elementKey) {
  const theme = (graphModel && graphModel.__epxTheme) || DEFAULT_THEME;
  return theme[elementKey] || {};
}

// ─── Pre-built Themes ─────────────────────────────────────────────

export const THEMES = {
  default: DEFAULT_THEME,

  dark: {
    startEvent:              { stroke: '#73d13d', fill: '#162312', strokeWidth: 1.5 },
    endEvent:                { stroke: '#ff4d4f', fill: '#2a1215', strokeWidth: 3 },
    intermediateEvent:       { stroke: '#40a9ff', fill: '#111d2c', strokeWidth: 1 },
    intermediateThrowEvent:  { stroke: '#40a9ff', fill: '#111d2c', strokeWidth: 1 },
    boundaryEvent:           { stroke: '#ff7a45', fill: '#2b1611', strokeWidth: 1.5 },

    userTask:         { stroke: '#40a9ff', fill: '#111d2c', strokeWidth: 1 },
    serviceTask:      { stroke: '#b37feb', fill: '#1a1325', strokeWidth: 1 },
    scriptTask:       { stroke: '#ffc53d', fill: '#2b2111', strokeWidth: 1 },
    manualTask:       { stroke: '#36cfc9', fill: '#112a27', strokeWidth: 1 },
    sendTask:         { stroke: '#597ef7', fill: '#131629', strokeWidth: 1 },
    receiveTask:      { stroke: '#69c0ff', fill: '#111d2c', strokeWidth: 1 },
    businessRuleTask: { stroke: '#ffd666', fill: '#2b2111', strokeWidth: 1 },
    callActivity:     { stroke: '#95de64', fill: '#162312', strokeWidth: 3 },

    exclusiveGateway:  { stroke: '#ffd666', fill: '#2b2111', strokeWidth: 2 },
    parallelGateway:   { stroke: '#36cfc9', fill: '#112a27', strokeWidth: 2 },
    inclusiveGateway:  { stroke: '#ff85c0', fill: '#291321', strokeWidth: 2 },
    complexGateway:    { stroke: '#8c8c8c', fill: '#1f1f1f', strokeWidth: 2 },
    eventBasedGateway: { stroke: '#95de64', fill: '#162312', strokeWidth: 2 },

    subProcess:   { stroke: '#1890ff', fill: '#111d2c', strokeWidth: 2 },
    transaction:  { stroke: '#1890ff', fill: '#111d2c', strokeWidth: 2 },
    participant:  { stroke: '#8c8c8c', fill: '#1f1f1f', strokeWidth: 2 },
    lane:         { stroke: '#595959', fill: '#141414', strokeWidth: 1 },

    dataObjectReference: { stroke: '#8c8c8c', fill: '#1f1f1f', strokeWidth: 1.5 },
    dataStoreReference:  { stroke: '#8c8c8c', fill: '#1f1f1f', strokeWidth: 1.5 },
    group:               { stroke: '#595959', fill: 'none',    strokeWidth: 1.5 },
    textAnnotation:      { stroke: '#8c8c8c', fill: 'none',    strokeWidth: 1.5 },

    sequenceFlow:    { stroke: '#8c8c8c', strokeWidth: 1.5 },
    messageFlow:     { stroke: '#40a9ff', strokeWidth: 1.5 },
    association:     { stroke: '#595959', strokeWidth: 1 },
    dataAssociation: { stroke: '#595959', strokeWidth: 1 },

    grid: { color: '#525252' },
    canvas: { background: '#1a1a1a' },
    text: { fill: '#e0e0e0' },

    panel: {
      background: '#1f1f1f',
      headerBackground: '#262626',
      headerBorder: '#404040',
      text: '#e0e0e0',
      textSecondary: '#bbb',
      textMuted: '#888',
      border: '#383838',
      inputBackground: '#2a2a2a',
      inputBorder: '#4a4a4a',
      hoverBackground: '#2f2f2f',
      accentBackground: '#111d2c',
      accentBorder: '#153450',
      accentText: '#40a9ff',
    },
  },

  highContrast: {
    startEvent:              { stroke: '#00c853', fill: '#e8f5e9', strokeWidth: 2 },
    endEvent:                { stroke: '#d50000', fill: '#ffebee', strokeWidth: 3 },
    intermediateEvent:       { stroke: '#2962ff', fill: '#e3f2fd', strokeWidth: 2 },
    intermediateThrowEvent:  { stroke: '#2962ff', fill: '#e3f2fd', strokeWidth: 2 },
    boundaryEvent:           { stroke: '#ff6d00', fill: '#fff3e0', strokeWidth: 2 },

    userTask:         { stroke: '#2962ff', fill: '#e3f2fd', strokeWidth: 2 },
    serviceTask:      { stroke: '#aa00ff', fill: '#f3e5f5', strokeWidth: 2 },
    scriptTask:       { stroke: '#ff6d00', fill: '#fff3e0', strokeWidth: 2 },
    manualTask:       { stroke: '#00bfa5', fill: '#e0f2f1', strokeWidth: 2 },
    sendTask:         { stroke: '#304ffe', fill: '#e8eaf6', strokeWidth: 2 },
    receiveTask:      { stroke: '#0091ea', fill: '#e1f5fe', strokeWidth: 2 },
    businessRuleTask: { stroke: '#ff8f00', fill: '#fff8e1', strokeWidth: 2 },
    callActivity:     { stroke: '#2e7d32', fill: '#e8f5e9', strokeWidth: 3 },

    exclusiveGateway:  { stroke: '#ff8f00', fill: '#fff8e1', strokeWidth: 2.5 },
    parallelGateway:   { stroke: '#00bfa5', fill: '#e0f2f1', strokeWidth: 2.5 },
    inclusiveGateway:  { stroke: '#c51162', fill: '#fce4ec', strokeWidth: 2.5 },
    complexGateway:    { stroke: '#424242', fill: '#f5f5f5', strokeWidth: 2.5 },
    eventBasedGateway: { stroke: '#2e7d32', fill: '#e8f5e9', strokeWidth: 2.5 },

    subProcess:   { stroke: '#1565c0', fill: '#e3f2fd', strokeWidth: 2.5 },
    transaction:  { stroke: '#1565c0', fill: '#e3f2fd', strokeWidth: 2.5 },
    participant:  { stroke: '#212121', fill: '#fafafa', strokeWidth: 2.5 },
    lane:         { stroke: '#212121', fill: '#fff',    strokeWidth: 1.5 },

    dataObjectReference: { stroke: '#424242', fill: '#fff',  strokeWidth: 2 },
    dataStoreReference:  { stroke: '#424242', fill: '#fff',  strokeWidth: 2 },
    group:               { stroke: '#424242', fill: 'none',  strokeWidth: 2 },
    textAnnotation:      { stroke: '#424242', fill: 'none',  strokeWidth: 2 },

    sequenceFlow:    { stroke: '#424242', strokeWidth: 2 },
    messageFlow:     { stroke: '#2962ff', strokeWidth: 2 },
    association:     { stroke: '#424242', strokeWidth: 1.5 },
    dataAssociation: { stroke: '#424242', strokeWidth: 1.5 },

    grid: { color: '#ccc' },
    canvas: { background: '#fff' },
    text: { fill: '#212121' },

    panel: {
      background: '#fff',
      headerBackground: '#fafafa',
      headerBorder: '#bbb',
      text: '#212121',
      textSecondary: '#424242',
      textMuted: '#757575',
      border: '#ccc',
      inputBackground: '#fff',
      inputBorder: '#999',
      hoverBackground: '#f5f5f5',
      accentBackground: '#e3f2fd',
      accentBorder: '#90caf9',
      accentText: '#1565c0',
    },
  },
};

// nord: removed — use epxFlow.registerTheme('nord', { ... }) from sfd-v2
// ocean: removed — use epxFlow.registerTheme('ocean', { ... }) from sfd-v2
// dracula: removed — use epxFlow.registerTheme('dracula', { ... }) from sfd-v2
// sunset: removed — use epxFlow.registerTheme('sunset', { ... }) from sfd-v2
// pastel: removed — use epxFlow.registerTheme('pastel', { ... }) from sfd-v2
